import React, { useCallback, useContext, useMemo } from "react";
import { toastSuccess, toastError } from "../../../../utils";
import AppContext from "../../../../context/appContext";

import {
  Box,
  Checkbox,
  Collapse,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { ChapterSection } from "./ChapterSection/ChapterSection";
import { Arrow } from "../Arrow/Arrow";
import { RenderDates } from "../RenderDates/RenderDates";
import PencilEditIcon from "../../../../components/VectorIcons/PencilEditIcon";
import { ReactComponent as AddendumIcon } from "../../../../assets/icons/addendum.svg";

import { PlanningGridContext } from "../PlanningGrid";
import { SelectionContext } from "../PlanningGrid";
import { ActionsMenu } from "../../../../components/ActionsMenu/ActionsMenu";
import {
  sortBlockChapters,
  getAllAttachments,
  toggleManyReducer,
  getBlockSelectionState,
} from "./BlockSection.utils";
import { moveChapterToAnotherBlock } from "../../../../services/api/attachments/attachments.api";

export const BlockSection = ({
  bloco,
  contentId,
  isFirstBlock,
  isLastBlock,
  handleEdit,
  handleDeleteChapter,
  previousBlockId,
  nextBlockId,
  refresh,
  handleDeleteAttachment,
  handleDeleteAddendum,
  attachmentActions,
  handleEditFile,
  handleOpenReleaseTexts,
}) => {
  const appContext = useContext(AppContext);
  const { headerGroups, grid, isSelectionMode, openIds, toggleOpenId } =
    useContext(PlanningGridContext);
  const { selectedFileIds, setSelectedFiles } = useContext(SelectionContext);

  const open = isSelectionMode || openIds.includes(`block-${bloco.id}`);

  const sortedChapters = useMemo(
    () => sortBlockChapters([...bloco.chapters]),
    [bloco.chapters]
  );

  const hasAddendumInBlock = useMemo(
    () => (bloco.chapters || []).some((chapter) => !!chapter.addenda?.length),
    [bloco.chapters]
  );

  const allAttachments = useMemo(() => getAllAttachments(bloco), [bloco]);

  const toggleMany = useCallback(
    (attachments) => {
      setSelectedFiles((prev) => toggleManyReducer(prev, attachments));
    },
    [setSelectedFiles]
  );

  const { allSelected: allBlockSelected, someSelected: someBlockSelected } =
    useMemo(
      () => getBlockSelectionState(allAttachments, selectedFileIds),
      [allAttachments, selectedFileIds]
    );

  const handleMoveToAnotherBlock = useCallback(
    async ({ chapter, block }) => {
      try {
        await moveChapterToAnotherBlock({
          blockId: bloco.id,
          chapterId: chapter.id,
          blockIdToMove: block,
        });
        appContext.setShowGlassLoading(false);
        toastSuccess("Capítulo movido com sucesso!");
        appContext.setCurrentStepBox("closed");
        refresh();
      } catch (error) {
        toastError("Erro ao mover capítulo!");
        console.error(error);
      }
    },
    [bloco.id, appContext, refresh]
  );

  return (
    <Box sx={{ mb: 1 }}>
      <Box
        display="grid"
        gridTemplateColumns={grid}
        sx={{
          bgcolor: open ? "#ffffff" : "#F6F6FE",
          borderBottom: "1px solid #e0e2ff",
          minHeight: "56px",
          pl: "16px",
          pr: "16px",
        }}
      >
        {isSelectionMode && (
          <Box display="flex" justifyContent="center" alignItems="center">
            <Checkbox
              checked={allBlockSelected}
              indeterminate={someBlockSelected}
              disabled={allAttachments.length === 0}
              onChange={() => toggleMany(allAttachments)}
              size="small"
              sx={{
                color: "#43444D",
                "&.Mui-checked": { color: "#43444D" },
                "&.MuiCheckbox-indeterminate": { color: "#43444D" },
                "&.Mui-disabled": { color: "#CCCCCC" },
              }}
            />
          </Box>
        )}
        <Box
          display="flex"
          alignItems="center"
          sx={{ cursor: "pointer" }}
          onClick={() => !isSelectionMode && toggleOpenId(`block-${bloco.id}`)}
        >
          {!isSelectionMode && (
            <IconButton size="small" disableRipple sx={{ p: 0 }}>
              <Arrow isOpen={open} margin="0 14px 0 16px" />
            </IconButton>
          )}
          <Typography
            fontWeight="bold"
            sx={{ fontFamily: "inherit", userSelect: "none" }}
          >
            {`Bloco ${bloco.number}`}
          </Typography>

          <Typography
            sx={{
              fontFamily: "inherit",
              textTransform: "uppercase",
              fontSize: "10px",
              color: "#58627D",
              ml: "24px",
              userSelect: "none",
            }}
          >
            {`${bloco.chapters?.length} capítulos`}
          </Typography>
        </Box>

        <RenderDates
          columns={headerGroups}
          planning={bloco.planning}
          realized={bloco.realized}
        />

        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          flex="1"
          sx={{ pr: "6px" }}
        >
          <Tooltip
            title="Possui adendo associado"
            placement="top"
            slotProps={{
              tooltip: {
                sx: {
                  bgcolor: "#FFFFFF",
                  color: "#1F1F29",
                  border: "1px solid #E2E4FF",
                  boxShadow: "0px 4px 12px rgba(31, 31, 41, 0.12)",
                  fontFamily: '"Globotipo Text", sans-serif',
                  fontSize: "12px",
                  fontWeight: 500,
                  p: "4px 6px",
                },
              },
              arrow: {
                sx: {
                  color: "#FFFFFF",
                  "&:before": {
                    border: "1px solid #E2E4FF",
                  },
                },
              },
            }}
            arrow
          >
            <Box
              component={AddendumIcon}
              aria-label="Adendo"
              sx={{
                mr: "3px",
                visibility: !open && hasAddendumInBlock ? "visible" : "hidden",
              }}
            />
          </Tooltip>
          <ActionsMenu
            menuOptions={[
              {
                action: () => handleEdit(bloco, "block"),
                label: "Editar",
                icon: <PencilEditIcon />,
              },
            ]}
            disabled={isSelectionMode}
          />
        </Box>
      </Box>

      <Collapse in={open} timeout={0} unmountOnExit>
        <Box>
          {sortedChapters.map((capitulo, index) => (
            <ChapterSection
              key={capitulo.id}
              bloco={bloco}
              capitulo={{
                ...capitulo,
                blockId: bloco.id,
              }}
              contentId={contentId}
              handleDeleteAttachment={handleDeleteAttachment}
              handleDeleteAddendum={handleDeleteAddendum}
              handleDeleteChapter={handleDeleteChapter}
              attachmentActions={attachmentActions}
              handleOpenReleaseTexts={handleOpenReleaseTexts}
              isLastBlock={isLastBlock}
              isLastChapter={index === sortedChapters.length - 1}
              canMoveToPreviousBlock={
                bloco.chapters?.length >= 2 && !index && !isFirstBlock
              }
              canMoveToNextBlock={
                bloco.chapters?.length >= 2 &&
                index === sortedChapters.length - 1 &&
                !isLastBlock
              }
              handleEditFile={handleEditFile}
              moveToPreviousBlock={() =>
                handleMoveToAnotherBlock({
                  chapter: capitulo,
                  block: previousBlockId,
                })
              }
              moveToNextBlock={() =>
                handleMoveToAnotherBlock({
                  chapter: capitulo,
                  block: nextBlockId,
                })
              }
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};
